import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Portfolio.css";
import portfolioVideo from "../../assets/Portfolio/Homeintro.mov";
import { API_BASE, getImageUrl } from "../../config/api";

// Category name → DB category string mapping
const SERVICES = [
  {
    id: 1,
    title: "Weddings",
    desc: "Celebrations of love, joy,\nand connection",
    route: "/wedding",
    category: "Wedding Photography",
  },
  {
    id: 2,
    title: "Cinematic Videography",
    desc: "Every frame told\nwith cinematic intent",
    route: "/cinematic",
    category: "Cinematic Videography",
  },
  {
    id: 3,
    title: "Baby & Newborn",
    desc: "Tender moments of new\nlife, softly preserved",
    route: "/babyshoot",
    category: "Baby & Newborn Shoot",
  },
  {
    id: 4,
    title: "Maternity",
    desc: "The quiet strength\nof becoming a mother",
    route: "/maternity",
    category: "Maternity Photography",
  },
  {
    id: 5,
    title: "Pre-Wedding",
    desc: "Your love story,\nbefore the big day",
    route: "/prewedding",
    category: "Pre-Wedding Shoot",
  },
  {
    id: 6,
    title: "Bharatanatyam",
    desc: "Timeless moments shared\nby those closest to you",
    route: "/bharatanatyam",
    category: "Bharatanatyam Photography",
  },
  {
    id: 7,
    title: "Corporate Events",
    desc: "Professional moments\ncaptured with purpose",
    route: "/corporate",
    category: "Corporate Events",
  },
];

const Portfolio = () => {
  const navigate   = useNavigate();
  const videoRef   = useRef(null);
  const rowRefs    = useRef([]);
  const hasInteracted = useRef(false);

  const [isMuted,    setIsMuted]    = useState(true);
  // categoryImages: { "Wedding Photography": ["url1", "url2"], ... }
  const [categoryImages, setCategoryImages] = useState({});

  // ── Fetch first 2 images for every category ──────────────
  useEffect(() => {
    const fetchAll = async () => {
      const results = {};
      await Promise.all(
        SERVICES.map(async (svc) => {
          try {
            const res  = await fetch(
              `${API_BASE}?category=${encodeURIComponent(svc.category)}`
            );
            const data = await res.json();
            // Filter image-type items only, take first 2
            const imgs = (Array.isArray(data) ? data : [])
              .filter((item) => item.type === "image" && item.image)
              .slice(0, 2)
              .map((item) => getImageUrl(item.image));
            results[svc.category] = imgs;
          } catch {
            results[svc.category] = [];
          }
        })
      );
      setCategoryImages(results);
    };
    fetchAll();
  }, []);

  // ── Video autoplay ────────────────────────────────────────
  useEffect(() => {
    const markInteracted = () => { hasInteracted.current = true; };
    window.addEventListener("click",   markInteracted, { once: true });
    window.addEventListener("scroll",  markInteracted, { once: true, passive: true });
    window.addEventListener("keydown", markInteracted, { once: true });

    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        if (hasInteracted.current) {
          video.muted = false;
          video.play().then(() => setIsMuted(false)).catch(() => {
            video.muted = true;
            video.play().catch(() => {});
            setIsMuted(true);
          });
        } else {
          video.muted = true;
          video.play().catch(() => {});
          setIsMuted(true);
        }
      } else {
        video.pause();
      }
    }, { threshold: 0.5 });

    observer.observe(video);

    return () => {
      window.removeEventListener("click",   markInteracted);
      window.removeEventListener("scroll",  markInteracted);
      window.removeEventListener("keydown", markInteracted);
      observer.disconnect();
    };
  }, []);

  // ── Toggle sound ──────────────────────────────────────────
  const toggleMute = () => {
    if (!videoRef.current) return;
    const newMuted = !isMuted;
    videoRef.current.muted = newMuted;
    if (!newMuted) videoRef.current.play().catch(() => {});
    setIsMuted(newMuted);
  };

  // ── Row scroll animation ──────────────────────────────────
  useEffect(() => {
    const observers = [];
    rowRefs.current.forEach((row, i) => {
      if (!row) return;
      const obs = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) { row.classList.add("ss-row--in"); obs.disconnect(); }
      }, { threshold: 0.12 });
      row.style.setProperty("--row-delay", `${i * 0.05}s`);
      obs.observe(row);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, []);

  return (
    <section className="portfolio-page">

      {/* HERO */}
      <div className="portfolio-hero">
        <video
          ref={videoRef}
          className="portfolio-video"
          autoPlay
          muted={isMuted}
          loop
          playsInline
          preload="auto"
        >
          <source src={portfolioVideo} type="video/mp4" />
        </video>

        <div className="portfolio-overlay" />

        <button className="sound-btn" onClick={toggleMute} aria-label="Toggle sound">
          <span className="sound-icon">
            {isMuted ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                <line x1="23" y1="9" x2="17" y2="15" />
                <line x1="17" y1="9" x2="23" y2="15" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
              </svg>
            )}
          </span>
          <span className="sound-text">{isMuted ? "SOUND OFF" : "SOUND ON"}</span>
        </button>

        <div className="portfolio-content">
          <div className="portfolio-bottom">
            <h1 className="portfolio-title">Our Works</h1>
          </div>
        </div>
      </div>

      {/* SERVICES */}
      <div className="ss-section">
        {SERVICES.map((svc, i) => {
          const isAlt  = i % 2 !== 0;
          const imgs   = categoryImages[svc.category] || [];
          const bigImg = imgs[0] || null;
          const smlImg = imgs[1] || imgs[0] || null; // fallback to first if only 1 image

          return (
            <div
              key={svc.id}
              className={`ss-row${isAlt ? " ss-row--alt" : ""}`}
              ref={(el) => (rowRefs.current[i] = el)}
            >
              {/* BIG PHOTO */}
              <div className="ss-big-photo">
                {bigImg ? (
                  <img src={bigImg} alt={svc.title} loading="lazy" />
                ) : (
                  <div className="ss-big-photo__placeholder" />
                )}
              </div>

              {/* RIGHT PANEL */}
              <div className="ss-panel">

                {/* SMALL PHOTO */}
                <div className="ss-small-photo">
                  {smlImg ? (
                    <img src={smlImg} alt={svc.title} loading="lazy" />
                  ) : (
                    <div className="ss-small-photo__placeholder" />
                  )}
                </div>

                <h2 className="ss-title">{svc.title}</h2>

                <p className="ss-desc">
                  {svc.desc.split("\n").map((line, j, arr) => (
                    <React.Fragment key={j}>
                      {line}
                      {j < arr.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </p>

                <button className="ss-btn" onClick={() => navigate(svc.route)}>
                  View Gallery
                </button>

              </div>
            </div>
          );
        })}
      </div>

    </section>
  );
};

export default Portfolio;